import * as React from 'react';

import type { CoachSuggestionComponent } from 'bundles/ai-coach-platform/components/patterns/suggestions/CoachSuggestion';
import { CoachSuggestionsContext } from 'bundles/ai-coach-platform/components/patterns/suggestions/context/coachSuggestionsContext';
import type { SuggestionAction } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import {
  getFirstUnresolvedSuggestionId,
  getUnresolvedSuggestions,
} from 'bundles/ai-coach-platform/components/patterns/suggestions/utils';
import usePrevious from 'bundles/common/hooks/usePrevious';

type Props = {
  expandedId?: string | null;
  onAction?: (id: string, action: SuggestionAction) => Promise<void> | void;
  onDisclosureToggle?: (id: string, expanded: boolean) => void;
  children: CoachSuggestionComponent[];
};

const CoachSuggestionsContextProvider = ({
  expandedId: controlledExpandedId,
  onAction = () => undefined,
  onDisclosureToggle = () => undefined,
  children,
}: React.PropsWithChildren<Props>) => {
  const isMounted = React.useRef<boolean>(false);
  const [expandedId, setExpandedId] = React.useState<string | null>(
    controlledExpandedId ?? getFirstUnresolvedSuggestionId(children) ?? null
  );
  // a11y: maintain map of suggestion node to set keyboard focus on resolution
  const suggestionNodesMap = React.useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    if (controlledExpandedId) {
      isMounted.current = true;
    }

    if (isMounted.current === false) {
      isMounted.current = true;
      // expand first unresolved suggestion on mount
      const firstUnresolvedSuggestionId = getFirstUnresolvedSuggestionId(children);
      if (firstUnresolvedSuggestionId) {
        onDisclosureToggle?.(firstUnresolvedSuggestionId, true);
      }
    } else if (getUnresolvedSuggestions(children).length === 0) {
      // reset state once all suggestions are resolved, this can happen for the "accept/reject all" case
      setExpandedId(null);
    }
  }, [children, onDisclosureToggle, controlledExpandedId]);

  const handleToggle = React.useCallback<(id: string) => void>(
    (id) => {
      // collapses current suggestion i.e. all suggestions collapsed
      const newExpandedSuggestionId = controlledExpandedId === id ? null : id;
      setExpandedId(newExpandedSuggestionId);

      onDisclosureToggle?.(id, !!newExpandedSuggestionId || false);
    },
    [controlledExpandedId, onDisclosureToggle]
  );

  const expandNextUnresolvedSuggestion = React.useCallback<(id: string) => void>(
    (id) => {
      const suggestion = children.find((s) => s.props.id === id);
      // find next unresolved in order based on position of current resolved suggestion, will cycle to first suggestion if currently on the last one
      const unresolved = getUnresolvedSuggestions(children);
      if (unresolved.length > 1) {
        const currentIndex = unresolved.findIndex((s) => s.props.id === suggestion?.props.id);
        const nextUnresolved = currentIndex + 1 === unresolved.length ? unresolved[0] : unresolved[currentIndex + 1];

        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        handleToggle(nextUnresolved.props.id);

        // programmatically focus on the header of next unresolved
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        const nextHeader = suggestionNodesMap.current[nextUnresolved.props.id];
        nextHeader?.focus();
      }
    },
    [children, handleToggle]
  );

  const previousActiveSuggestionId = usePrevious(controlledExpandedId);
  React.useEffect(() => {
    if (controlledExpandedId !== undefined && controlledExpandedId !== previousActiveSuggestionId) {
      setExpandedId(controlledExpandedId);
    }
  }, [controlledExpandedId, previousActiveSuggestionId]);

  const handleAction = React.useCallback<(id: string, action: SuggestionAction) => Promise<void>>(
    // Gated on a resolved result: expandNextUnresolvedSuggestion only runs in the .then(), so a
    // rejected onAction (e.g. a failed mutation) never advances focus. No error handling here
    // the rejection just propagates to the caller, which already handles it
    (id, action) => Promise.resolve(onAction(id, action)).then(() => expandNextUnresolvedSuggestion(id)),
    [onAction, expandNextUnresolvedSuggestion]
  );

  const getIndex = React.useCallback<(id: string) => number>(
    (id) => children.findIndex((s) => s.props.id === id),
    [children]
  );

  const updateSuggestionRef = React.useCallback<(id: string, ref: HTMLDivElement | null) => void>((id, ref) => {
    if (!ref) {
      delete suggestionNodesMap.current[id];
      return;
    }
    suggestionNodesMap.current[id] = ref;
  }, []);

  return (
    <CoachSuggestionsContext.Provider
      value={{
        expandedId,
        getIndex,
        setExpandedId,
        updateSuggestionRef,
        onAction: handleAction,
        onDisclosureToggle: handleToggle,
      }}
    >
      {children}
    </CoachSuggestionsContext.Provider>
  );
};

export default CoachSuggestionsContextProvider;
